import type { AdminAgentConversation } from "../models/admin-agent.models";
import AdminAgentMessage from "./AdminAgentMessage";
import { useEffect, useRef } from "react";

// Follow new steps while the reader is near the latest message; preserve their position when reviewing earlier work.
const AdminAgentMessages = ({ conversation, loading }: { conversation?: AdminAgentConversation; loading: boolean }) => {
  const scroller = useRef<HTMLDivElement>(null);
  const followLatest = useRef(true);
  const previousId = useRef<string>();
  const messageCount = conversation?.messages.length ?? 0;

  useEffect(() => {
    const container = scroller.current;
    const changedChat = previousId.current !== conversation?.id;
    previousId.current = conversation?.id;

    if (container && (changedChat || followLatest.current)) {
      container.scrollTop = container.scrollHeight;
      followLatest.current = true;
    }
  }, [conversation?.id, messageCount]);

  const trackReadingPosition = () => {
    const container = scroller.current;

    if (container) {
      followLatest.current = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    }
  };

  return (
    <div
      ref={scroller}
      onScroll={trackReadingPosition}
      data-test="admin-agent-messages"
      aria-label="Conversation messages"
      aria-busy={loading}
      className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-2"
    >
      {loading && !conversation && (
        <p role="status" className="text-sm text-gray-500 dark:text-gray-400">
          Loading chat…
        </p>
      )}
      {!loading && !conversation && (
        <p data-test="admin-agent-empty" className="text-sm leading-6 text-gray-500 dark:text-gray-400">
          Create a chat to start a task, or select an available chat to continue.
        </p>
      )}
      {conversation && messageCount === 0 && (
        <div
          data-test="admin-agent-chat-empty"
          className="space-y-2 text-sm leading-6 text-gray-500 dark:text-gray-400"
        >
          <p>
            Ask a task. The assistant can use permitted read tools, inspect their results, and continue through several
            steps.
          </p>
          <p className="rounded border border-dashed border-gray-300 px-3 py-2 text-xs dark:border-gray-600">
            Try: “Check runtime and list admin features, then explain the default Admin permissions.”
          </p>
        </div>
      )}
      {conversation?.messages.map((message) => <AdminAgentMessage key={message.id} message={message} />)}
      {conversation?.status === "running" && (
        <p
          role="status"
          data-test="admin-agent-working"
          className="text-xs font-medium text-green dark:text-emerald-300"
        >
          Working…
        </p>
      )}
    </div>
  );
};

export default AdminAgentMessages;
